import { apiClient, ApiClient } from "@/lib/api/api-client";
import { createStorageService } from "@/lib/storage/gcs/googleCloudStorage";
import { IStorageService } from "@/lib/storage/storage";
import { Conversation } from "@/modules/types/conversation/conversation";
import {
  Attachment,
  Message,
  MessageCreate,
} from "@/modules/types/conversation/message";
import { BaseResponse } from "@/modules/types/response";

export interface ListConversationsResp extends BaseResponse {
  conversations: Conversation[];
  total: number;
}

export interface ListMessagesResp extends BaseResponse {
  messages: Message[];
  total: number;
  nextId?: string;
}

export interface MessageDataResponse extends BaseResponse {
  messageData: Message;
}

export interface ListAttachmentsResp extends BaseResponse {
  attachments: Attachment[];
  total: number;
  nextId?: string;
}

export interface UnreadConversationResp extends BaseResponse {
  total: number;
}

export class ConversationService {
  private BASE_PATH =
    process.env.API_CONVERSATION_ENDPOINT ||
    "http://localhost:8081/conversation";
  private storage: IStorageService = createStorageService();
  private destination: string = "conversations/";
  private apiClient: ApiClient = apiClient;
  private apiVersion = "v1.0.0";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async getConversations({
    candidateId,
    jobId,
    jobTitle,
    limit,
    offset,
  }: {
    candidateId: string;
    jobId?: string;
    jobTitle?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListConversationsResp> {
    const params = new URLSearchParams();
    params.append("candidateId", candidateId);
    if (jobId && jobId.length > 0) params.append("jobID", jobId);
    if (jobTitle && jobTitle.length > 0) params.append("jobTitle", jobTitle);
    if (limit && limit > 0) params.append("limit", limit.toString());
    if (offset && offset > 0) params.append("offset", offset.toString());
    return this.apiClient.fetch<ListConversationsResp>(
      `${this.BASE_PATH}/${this.apiVersion}/list?${params.toString()}`,
      {
        method: "GET",
      }
    );
  }

  async getMessageByConversationId(
    conversationId: string,
    limit?: number,
    nextId?: string // fetch next page of messages
  ): Promise<ListMessagesResp> {
    const params = new URLSearchParams();
    if (limit && limit > 0) params.append("limit", limit.toString());
    if (nextId && nextId.length > 0) params.append("nextId", nextId);
    return this.apiClient.fetch<ListMessagesResp>(
      `${this.BASE_PATH}/${
        this.apiVersion
      }/${conversationId}/messages?${params.toString()}`,
      {
        method: "GET",
      }
    );
  }

  async sendMessage(message: MessageCreate): Promise<MessageDataResponse> {
    return this.apiClient.fetch<MessageDataResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/send-message`,
      {
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(message),
      }
    );
  }

  async getAttachmentsOfConversation(
    conversationId: string,
    limit?: number,
    nextId?: string
  ): Promise<ListAttachmentsResp> {
    const params = new URLSearchParams();
    if (limit && limit > 0) params.append("limit", limit.toString());
    if (nextId && nextId.length > 0) params.append("nextId", nextId);
    return this.apiClient.fetch<ListAttachmentsResp>(
      `${this.BASE_PATH}/${
        this.apiVersion
      }/${conversationId}/attachments?${params.toString()}`,
      {
        method: "GET",
      }
    );
  }

  async markConversationAsRead(
    conversationId: string,
    businessId: string,
    businessFullName: string
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/${conversationId}/mark-read`,
      {
        method: "POST",
        body: JSON.stringify({
          seenUserId: businessId,
          senderName: businessFullName,
        }),
      }
    );
  }

  async getTotalUnreadConversation(
    candidateId: string
  ): Promise<UnreadConversationResp> {
    return this.apiClient.fetch<UnreadConversationResp>(
      `${this.BASE_PATH}/${this.apiVersion}/unread-total?candidateId=${candidateId}`,
      {
        method: "GET",
      }
    );
  }

  async uploadFile(file: File, conversationId: string, fileName: string) {
    const filePath = `${this.destination}${conversationId}/${fileName}`;
    return await this.storage.uploadPrivateFile(file, filePath);
  }

  async getFileData(filePath: string) {
    return await this.storage.getPrivateFileSignedUrl(filePath);
  }

  async downloadFile(filePath: string) {
    return await this.storage.downloadPrivateFile(filePath);
  }
}

export const conversationService = new ConversationService();
